import React from 'react';
import dataModel from '../../models/DataModel';
import AnalyticsLogger from '../analytics/analyticsLogger';  
import TextMap from '../../helpers/TextMap';

const completionCiteriaSelector = {
  completion_criterion: {
    attachments: { additionalFields: ['upload', 'photo_longitude', 'photo_latitude', 'photo_taken_at'] } ,
    comments: {
      user: { },
    },
  },
};

function handleAnswerSelected(completionStep, response, updateCallback) {
  if (completionStep.completion_type == 'inspection' || completionStep.completion_type == 'boolean') {
    if (completionStep.response || response == false) {
      handleChangeAnswer(completionStep, response, null, updateCallback);
    }
    else {
      handleCompleteStep(completionStep, response, updateCallback);
    }
  }
  else if (completionStep.requested_file_type == 'jpg') {
    if (response == 'N/A') {
      if (completionStep.not_applicable) {
        completionStep.not_applicable = false;
        handleChangeAnswer(completionStep, 'not_applicable', 'Are you sure you want to uncheck No Photos Required for the completion step', updateCallback);
      }
      else {
        completionStep.not_applicable = true;
        handleCompleteStep(completionStep, 'not_applicable', updateCallback);
      }
    }
  }
}

function handleChangeAnswer(completionStep, response, messageContent, updateCallback) {
  const stepName = completionStep.name
    .split(' ')
    .map(
      word => word.substr(0, 1).toUpperCase() + word.substr(1).toLowerCase()
    )
    .join(' ');

  const message = (
    <div>
      <p>{(messageContent ? messageContent : 'Are you sure you want to uncheck the completion step') + ' ' + stepName + '?'}</p>
    </div>
  );

  dataModel.showGlobalModal('SYSTEM_POPUP', {
    height: 184,
    width: 320,
    message: message,
    cancelButtonText: 'Cancel',
    okButtonText: 'Yes',
    okCallback: updateCallback,
    handleOwnerOk: () => {
      handleCompleteStep(completionStep, response, updateCallback);
    },
    handleOwnerCancel: () => {
      cancelChange();
    },
  });
};

function cancelChange() {
  //close out of popup, dont do anything
};

function handleCompleteStep(completionStep, response, updateCallback) {
  let editCompletionStep = Object.assign({}, completionStep);
  const analyticsLogger = new AnalyticsLogger({       
    currentRole: null,
    isMobile: !!window.cordova,
  });
  
  if (response != false) {
    editCompletionStep.completed_at = new Date().toJSON();
  }
  else {
    editCompletionStep.completed_at = null;
  }
  if (response != 'not_applicable') {
    editCompletionStep.response = response;
  }

  // To speed up the display
  if (updateCallback) {
    updateCallback(editCompletionStep);
  }

  if (response == 'not_applicable') {
    if (!editCompletionStep.not_applicable) {
      dataModel.completionCriteriaDAO.unmarkNotApplicable(editCompletionStep).then(completionStep => {
        analyticsLogger.logGenericCSEvent('QuickActionsNA');
        if (updateCallback) {
          updateCallback(completionStep);
        }
      });
    }
    else {
      dataModel.completionCriteriaDAO.markNotApplicable(editCompletionStep).then(completionStep => {
        analyticsLogger.logGenericCSEvent('QuickActionsNA');
        if (updateCallback) {
          updateCallback(completionStep);
        }
      });
    }
  }
  else {
    const analyticsLabel = determineAnalyticsLabel(response);
    analyticsLogger.logGenericCSEvent(analyticsLabel);
    dataModel.completionCriteriaDAO.completeCompletionCriterion(editCompletionStep, response).then(completionStep => {
      if (updateCallback) {
        updateCallback(completionStep);
      }
    });
  }
}

function  determineAnalyticsLabel(response) {
  if (response == true) {
    return 'QuickActionsCheckoff';
  } else if (!response) {
    return 'QuickActionsUndo';
  } else {
    return 'QuickActionsInspection';
  }
}

function getStatusObject(completionStep) {
  const textMap = new TextMap();
  switch (completionStep.status) {
  case 'rejected': 
    return { text: textMap.rejected, css: 'rejected' };
  case 'not_started':
    return { text: textMap.notStarted, css: 'not-started' };
  case 'pending_review':
    return { text: textMap.pendingReview, css: 'pending' };
  case 'pending_approval':
    return { text: textMap.pendingApproval, css: 'pending' };
  case 'approved':
    return { text: textMap.approved, css: 'approved' };
  default :
    return { text: textMap.completed, css: 'approved' };
  }
}

function handleCSNotApplicableButton(completionStep, analyticsLogger) {
  completionStep.not_applicable = true;
  completionStep.completed_at = new Date().toJSON();
  switch (completionStep.completion_type) {
  case 'boolean':
    analyticsLogger.logNotApplicable('CheckoffNotApplicable', completionStep.requested_file_type);
    break;
  case 'file_upload':
    analyticsLogger.logNotApplicable('UploadNotApplicable', completionStep.requested_file_type);
    break;
  case 'form':
    analyticsLogger.logNotApplicable('FormNotApplicable', completionStep.requested_file_type);
    break;
  case 'inspection':
    analyticsLogger.logNotApplicable('InspectionNotApplicable', completionStep.requested_file_type);
    break;
  default:
    break;
  }
  return dataModel.completionCriteriaDAO.markNotApplicable(completionStep);
}

function handleCSUndoNotApplicableButton(completionStep, analyticsLogger) {
  completionStep.not_applicable = null;
  completionStep.completed_at = null;
  switch (completionStep.completion_type) {
  case 'boolean':
    analyticsLogger.logNotApplicable('N/AUndo', completionStep.requested_file_type);
    break;
  case 'file_upload':
    analyticsLogger.logNotApplicable('N/AUndo', completionStep.requested_file_type);
    break;
  case 'form':
    analyticsLogger.logNotApplicable('N/AUndo', completionStep.requested_file_type);
    break;
  case 'inspection':
    analyticsLogger.logNotApplicable('N/AUndo', completionStep.requested_file_type);
    break;
  default:
    break;
  }
  return dataModel.completionCriteriaDAO.unmarkNotApplicable(completionStep);
}

async function handleReviewed(completionStep, analyticsLogger=null, options) {
  completionStep.reviewed_at = new Date().toJSON();
  if (analyticsLogger) {
    analyticsLogger.logReviewEvent();
  }
  // (completionCriterion, ignoreAnalytics, dontQueue, eventMetadatum, options) 
  return await dataModel.completionCriteriaDAO.reviewCompletionCriterion(completionStep, null, null, null, options);
}

async function handleApproved(completionStep, analyticsLogger=null, options) {
  completionStep.approved_at = new Date().toJSON();
  if (analyticsLogger) {
    analyticsLogger.logReviewEvent();
  }
  // (completionCriterion, ignoreAnalytics, dontQueue, eventMetadatum, options) 
  return await dataModel.completionCriteriaDAO.approveCompletionCriterion(completionStep, null, null, null, options);
}

function handleRejected(user, task, completionStep, type, commentText, analyticsLogger, sendComment) {
  if (commentText && sendComment) {
    const comment = {
      caption: type == 'na' ? 'NO PHOTO REQUIRED' : 'REJECTED',
      body: commentText,
      commentable_type: 'CompletionCriterion',
      commentable_id: completionStep.id,
      user_id: user.id,
    };
    dataModel.commentsDAO.createComment(comment, false, null, { recordDescriptor: { comments: {} } });
  }

  if (type == 'reject') {
    if (analyticsLogger && (task.status == 'pending_review' || task.status == 'pending_approval')){
      analyticsLogger.logRejectionEvent();
    }
    return dataModel.completionCriteriaDAO.rejectCompletionCriterion(
      completionStep, 
      commentText ? commentText : 'No reason supplied',
      null, null, null, 
      { recordDescriptor: completionCiteriaSelector }
    );
  }
  else {
    return Promise.resolve();
  }
}

export default {
  handleAnswerSelected,
  getStatusObject,
  handleCSNotApplicableButton,
  handleChangeAnswer,
  handleReviewed,
  handleApproved,
  handleCSUndoNotApplicableButton,
  handleRejected,
};



// WEBPACK FOOTER //
// ./src/helpers/CompletionStepUtil/CompletionStepUtil.js